import type {
  A2UIEnvelope,
  Action,
  ConversationHistoryMessage,
  FeedbackRating,
  Source,
  SourcesSummary,
} from '@cowbell/moo';

/** V1 server-stored message shape (subset). Reasoning and tool calls are
 *  observability for streaming and stay dropped on hydrate. Persisted card
 *  payloads, source citations, and the opaque sources summary do survive
 *  (slice 2c addition) so historical messages render the same affordances
 *  as live ones. */
interface ServerHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  messageId?: string;
  correlationId?: string;
  feedbackRating?: FeedbackRating | null;
  actions?: Action[];
  sources?: Source[];
  sourcesSummary?: SourcesSummary;
  a2uiMessages?: A2UIEnvelope[];
  reaction?: string | null;
}

type Part = { type: string; text?: string; data?: Record<string, unknown> };

function doneData(m: ServerHistoryMessage): Record<string, string> | null {
  const data: Record<string, string> = {};
  if (m.messageId) data.messageId = m.messageId;
  if (m.correlationId) data.correlationId = m.correlationId;
  return Object.keys(data).length === 0 ? null : data;
}

/** Builds the `data-*` parts for an assistant-side history message — the
 *  same affordances `buildAssistantMessageMeta` rebuilds for live messages
 *  so historical ones render the feedback / cards / sources UI identically. */
function assistantSidecarParts(m: ServerHistoryMessage): Part[] {
  const parts: Part[] = [];
  if (m.feedbackRating !== undefined) {
    parts.push({
      type: 'data-feedback-rating',
      data: { rating: m.feedbackRating },
    });
  }
  if (m.actions?.length) {
    parts.push({ type: 'data-actions', data: { actions: m.actions } });
  }
  if (m.sources?.length) {
    parts.push({ type: 'data-sources', data: { sources: m.sources } });
  }
  if (m.sourcesSummary) {
    parts.push({
      type: 'data-sources-summary',
      data: m.sourcesSummary as unknown as Record<string, unknown>,
    });
  }
  if (m.a2uiMessages?.length) {
    for (const env of m.a2uiMessages) {
      parts.push({ type: 'data-a2ui', data: env as Record<string, unknown> });
    }
  }
  const data = doneData(m);
  if (data) parts.push({ type: 'data-done', data });
  return parts;
}

/** Translates the V1 conversation `messages` payload into the AI-SDK
 *  `UIMessage` shape consumed by `useChat({ initialMessages })`.
 *
 *  Tool calls / reasoning are dropped — observability for streaming, not
 *  persisted output. Feedback rating + action cards + sources + sources
 *  summary survive as `data-*` parts so `buildAssistantMessageMeta` rebuilds
 *  the same meta shape for historical messages as live ones. */
export function historyToUIMessages(
  msgs: ServerHistoryMessage[]
): ConversationHistoryMessage[] {
  return msgs.map((m, i) => {
    const parts: Part[] = [{ type: 'text', text: m.content }];
    if (m.role === 'assistant') {
      parts.push(...assistantSidecarParts(m));
    }
    if (m.role === 'user' && m.reaction) {
      parts.push({ type: 'data-reaction', data: { emoji: m.reaction } });
    }
    return {
      id: m.messageId ?? `hist-${i}`,
      role: m.role,
      parts,
    };
  });
}
